import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { ServiceSession, MileageData } from '../types';
import { useAuth } from './AuthContext';
import { subscribeGiftClaim } from '../utils/giftVoucherClaim';
import { applyGiftWalletToCartItems, cartWalletMethodsChanged } from '../utils/giftCartWallet';
import i18n from '../config/i18n';

export interface CartItem {
  cartItemId: string;
  proId: number;
  proSlug: string;
  proName: string;
  serviceId?: number;
  serviceName?: string;
  price: number;
  date?: string;
  time?: string;
  sessionId?: number;
  session?: ServiceSession;
  locationId?: number;
  locationName?: string;
  locationAddress?: string;
  employeeId?: number;
  isHome: boolean;
  isOnline?: boolean;
  servicePhoto?: string;
  creditPrice?: number | null;
  // These might be filled during checkout
  animalId?: number;
  service_address?: string;
  client_phone?: string;
  clientAnswer?: string;
  allowOnlinePayment?: boolean;
  /** Ticket #45 — pro autorise CB + sur place ; respecté avec payment_method */
  allowClientPaymentChoice?: boolean;
  payment_method?: 'classic' | 'stripe' | 'forfait' | 'credits';
  pro_address?: string;
  mileageData?: MileageData | null;
  forfaitId?: number;
  packId?: number;
}

interface CartContextType {
  items: CartItem[];
  /** Horodatage du dernier ajout réussi (surbrillance #839). */
  lastAddedAtMs: number | null;
  /** Dernier article ajouté (#939 — autre créneau même service). */
  lastAddedItem: Omit<CartItem, 'cartItemId'> | null;
  addItem: (item: Omit<CartItem, 'cartItemId'>) => boolean;
  removeItem: (cartItemId: string) => void;
  clearCart: () => void;
  /**
   * Seed le panier avec un item provenant d'un handoff cross-origin
   * (widget embarqué → checkout). Idempotent : si un item équivalent existe
   * déjà (même proId + serviceId + date + time + sessionId), no-op.
   * Retourne `true` si l'item a été inséré, `false` sinon (déjà présent ou
   * bloqué par la règle "un seul pro par panier").
   */
  seedFromHandoff: (item: Omit<CartItem, 'cartItemId'>) => boolean;
  totalPrice: number;
  itemCount: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // ⚠️ Lazy initializer (et NON useEffect "load") : le state est peuplé
  // SYNCHRONIQUEMENT depuis localStorage avant tout commit/effect. Sans ça,
  // on avait une race condition : les effects enfants (ex: `CheckoutPage`
  // qui appelle `seedFromHandoff` au mount pour récupérer le panier via le
  // fragment `#cart=...` posé par le widget) s'exécutent AVANT les effects
  // parents en React. Le seed était donc immédiatement écrasé par le
  // `setItems(savedCart)` du CartProvider — résultant en un panier qui
  // semblait vide bien que l'item ait été correctement ajouté côté iframe.
  const [lastAddedAtMs, setLastAddedAtMs] = useState<number | null>(null);
  const [lastAddedItem, setLastAddedItem] = useState<Omit<CartItem, 'cartItemId'> | null>(null);
  const [items, setItems] = useState<CartItem[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const saved = window.localStorage.getItem('rintintin_cart');
      if (!saved) return [];
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      // localStorage indisponible (mode privé Safari sur certaines versions),
      // ou JSON corrompu — on démarre avec un panier vide plutôt que crasher.
      console.error('Failed to parse cart from localStorage', e);
      return [];
    }
  });

  // Sync cart to localStorage whenever it changes
  useEffect(() => {
    try {
      window.localStorage.setItem('rintintin_cart', JSON.stringify(items));
    } catch {
      // localStorage write peut échouer (quota, mode privé) — on ne casse pas
      // l'UX, le panier reste en mémoire jusqu'au prochain reload.
    }
  }, [items]);

  const { user } = useAuth();
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const syncCartWallet = useCallback((userId: number) => {
    void (async () => {
      const current = itemsRef.current;
      if (!userId || current.length === 0) return;
      const next = await applyGiftWalletToCartItems(userId, current);
      if (cartWalletMethodsChanged(current, next)) {
        setItems(next);
      }
    })();
  }, []);

  useEffect(() => {
    if (!user?.user_id) return;
    syncCartWallet(user.user_id);
  }, [user?.user_id, syncCartWallet]);

  useEffect(() => {
    if (!user?.user_id) return;
    const userId = user.user_id;
    return subscribeGiftClaim(() => {
      syncCartWallet(userId);
    });
  }, [user?.user_id, syncCartWallet]);

  const addItem = useCallback(
    (item: Omit<CartItem, 'cartItemId'>): boolean => {
      let blocked = false;
      let added = false;

      setItems(prev => {
        const existingProId = prev.length > 0 ? prev[0].proId : null;

        if (existingProId !== null && existingProId !== item.proId) {
          blocked = true;
          return prev;
        }

        added = true;
        return [...prev, { ...item, cartItemId: Math.random().toString(36).substr(2, 9) }];
      });

      if (blocked) {
        const existingProName =
          items.length > 0
            ? items[0].proName
            : i18n.t('booking:cart.otherProFallback');
        toast.error(
          i18n.t('booking:cart.mixedProsError', { proName: existingProName }),
          { autoClose: 6000 }
        );
        return false;
      }

      if (added) {
        setLastAddedAtMs(Date.now());
        setLastAddedItem(item);
      }

      return added;
    },
    [items]
  );

  /**
   * Variante de `addItem` pensée pour le handoff URL du widget embarqué.
   *
   * Différences vs `addItem` :
   *  - Dédup forte : si un item "équivalent" est déjà dans le panier, on ne
   *    re-ajoute pas (évite les doublons si le user navigue puis revient sur
   *    /checkout, ou si React Strict Mode joue le useEffect deux fois en dev).
   *  - Pas de toast d'erreur silencieux : le caller (CheckoutPage) gère
   *    l'UX selon le retour.
   */
  const seedFromHandoff = useCallback((item: Omit<CartItem, 'cartItemId'>): boolean => {
    let result = false;

    setItems(prev => {
      // Règle "un seul pro par panier" — même règle que addItem.
      const existingProId = prev.length > 0 ? prev[0].proId : null;
      if (existingProId !== null && existingProId !== item.proId) {
        return prev;
      }

      // Dédup : on considère deux items équivalents s'ils partagent pro +
      // service + créneau. Suffisant pour le handoff (le widget pousse
      // toujours UN seul item à la fois) sans bloquer un user qui voudrait
      // ajouter manuellement un 2e item différent sur le même service.
      const isDuplicate = prev.some(
        existing =>
          existing.proId === item.proId &&
          existing.serviceId === item.serviceId &&
          existing.date === item.date &&
          existing.time === item.time &&
          (existing.sessionId ?? null) === (item.sessionId ?? null)
      );
      if (isDuplicate) {
        return prev;
      }

      result = true;
      return [...prev, { ...item, cartItemId: Math.random().toString(36).substr(2, 9) }];
    });

    if (result) {
      setLastAddedAtMs(Date.now());
      setLastAddedItem(item);
    }

    return result;
  }, []);

  const removeItem = useCallback((cartItemId: string) => {
    setItems(prev => prev.filter(item => item.cartItemId !== cartItemId));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setLastAddedAtMs(null);
    setLastAddedItem(null);
  }, []);

  const totalPrice = items.reduce((sum, item) => sum + item.price, 0);
  const itemCount = items.length;

  return (
    <CartContext.Provider
      value={{
        items,
        lastAddedAtMs,
        lastAddedItem,
        addItem,
        seedFromHandoff,
        removeItem,
        clearCart,
        totalPrice,
        itemCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
